import { useState, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import Select from 'react-select'; // ✅ Multi-select dropdown
import studyDirectionsData from './data/studyDirections.json'; // ✅ Import JSON file
import icBooks from './assets/img/ic-books.svg';
import icDelete from './assets/img/ic-delete-danger.svg';

const CompanyAddAreasDirections = ({ formData = {}, handleChange, handleAreasSubmit, isEditPage, setFormData }) => { // ✅ Ensure formData has a default value
    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values
    const [specialistAreas, setSpecialistAreas] = useState([]); // ✅ Store filtered specialist areas

    // ✅ Update Specialist Areas when Study Direction changes
    useEffect(() => {
        if (formData.studyDirections) {
            setSpecialistAreas(studyDirectionsData[formData.studyDirections] || []);
        }
    }, [formData.studyDirections]);

    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleAreasSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleAreasSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleAreasSubmit is not a function.');
        }
    };

    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);

        if (typeof setFormData === 'function') {
            setFormData({ studyFields: [...tempFormData.studyFields] }); // ✅ Restore studyFields array
        }
    };

    const onAddStudyFields = () => {
        setFormData((prev) => ({
            ...prev,
            studyFields: [
                ...prev.studyFields,
                {
                    studyField: '',
                    specialistAreas: '',
                    preferredStudyDirections: '',
                },
            ],
        }));
    };

    const onRemoveStudyFields = (indexToRemove) => {
        setFormData((prevData) => ({
            ...prevData,
            studyFields: prevData.studyFields.filter((_, index) => index !== indexToRemove),
        }));
    };

    return (
        <> 
            <Card>
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">4.</span> Specialist areas & study directions</h3>
                            <hr />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            {isEditing ? (
                                <>
                                    {Array.isArray(formData.studyFields) && formData.studyFields.length > 0 ? (
                                        formData.studyFields.map((entry, index) => (
                                            <Card
                                                key={index}
                                                className={index !== formData.studyFields.length - 1 ? 'mb-3' : ''}
                                            >
                                                <Card.Body>
                                                    <Row className="mb-3">
                                                        <div key={`${index}-${entry.studyField}-${entry.specialistAreas}-${entry.preferredStudyDirections}`}>
                                                            <Row className="mb-3">
                                                                <Col md={6}>
                                                                    {/* ✅ Study Direction */}
                                                                    <Form.Group controlId={`studyField-${index}`}>
                                                                        <Form.Label>Study Direction</Form.Label>
                                                                        <Select
                                                                                options={Object.keys(studyDirectionsData).map((key) => ({
                                                                                value: key,
                                                                                label: key,
                                                                            }))}
                                                                            value={entry.studyField ? { value: entry.studyField, label: entry.studyField } : null}
                                                                            onChange={(selected) =>
                                                                                isEditing && handleChange(index, 'studyField', selected?.value || '')
                                                                            }
                                                                            isClearable
                                                                            isDisabled={!isEditing}
                                                                            className="react-select"
                                                                            classNamePrefix="react-select"
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col md={6}>
                                                                    {/* ✅ Specialist Area */}
                                                                    <Form.Group controlId={`specialistAreas-${index}`}>
                                                                        <Form.Label>Specialist Area</Form.Label>
                                                                        <Select
                                                                            options={Object.keys(studyDirectionsData).map((key) => ({
                                                                                value: key,
                                                                                label: key,
                                                                            }))}
                                                                            value={
                                                                                entry.specialistAreas
                                                                                ? { value: entry.specialistAreas, label: entry.specialistAreas }
                                                                                : null
                                                                            }
                                                                            onChange={(selected) =>
                                                                                isEditing &&
                                                                                handleChange(index, 'specialistAreas', selected?.value || '')
                                                                            }
                                                                            isClearable
                                                                            isDisabled={!isEditing}
                                                                            className="react-select"
                                                                            classNamePrefix="react-select"
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col md={12}>
                                                                    {/* ✅ Preferred Study Direction */}
                                                                    <Form.Group controlId={`preferredStudyDirections-${index}`}>
                                                                        <Form.Label>Preferred Study Direction</Form.Label>
                                                                        <Select
                                                                            options={
                                                                                Array.isArray(studyDirectionsData[entry.specialistAreas])
                                                                                ? studyDirectionsData[entry.specialistAreas].map((area) => ({
                                                                                    value: area,
                                                                                    label: area,
                                                                                    }))
                                                                                : []
                                                                            }
                                                                            value={
                                                                                entry.preferredStudyDirections
                                                                                ? { value: entry.preferredStudyDirections, label: entry.preferredStudyDirections }
                                                                                : null
                                                                            }
                                                                            onChange={(selected) =>
                                                                                isEditing &&
                                                                                handleChange(index, 'preferredStudyDirections', selected?.value || '')
                                                                            }
                                                                            isClearable
                                                                            isDisabled={!isEditing || !entry.specialistAreas}
                                                                            className="react-select"
                                                                            classNamePrefix="react-select"
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </Row>
                                                    {index > 0 && (
                                                        <Row>
                                                            <Col className="text-end">
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    onClick={() => onRemoveStudyFields(index)}
                                                                    className="align-center text-danger p-0"
                                                                >
                                                                    <Image src={icDelete} alt="Remove" className="me-1" />
                                                                    Remove specialist area
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                        
                                                    )}  
                                                </Card.Body>
                                            </Card>
                                        ))
                                    ) : (
                                        <Card className="mb-3">
                                            <Card.Body>
                                            <Row>
                                                <Col>
                                                <p>No specialist areas or study directions added yet.</p>
                                                {isEditing && (
                                                    <Button variant="primary" onClick={onAddStudyFields}>
                                                    Add Study Field
                                                    </Button>
                                                )}
                                                </Col>
                                            </Row>
                                            </Card.Body>
                                        </Card>
                                    )}
                                    {isEditing && (
                                        <Row className="mt-4">
                                            <Col md={12} className="justify-content-end d-flex">
                                                <Button
                                                    variant="primary"
                                                    onClick={onAddStudyFields}
                                                    className="btn-sm"
                                                >
                                                    Add new specialist areas
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}   
                                </>
                            ) : (
                                <>
                                {/* not editing */}
                                    {Array.isArray(formData.studyFields) && formData.studyFields.length > 0 ? (
                                        formData.studyFields.map((field, index) => (
                                            <div
                                                key={index}
                                                className={index !== formData.studyFields.length - 1 ? 'mb-3' : ''}
                                            >
                                                <Row>
                                                    <Col md={12}>
                                                        <Card>
                                                            <Card.Body className="p-3">
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        <Image src={icBooks} alt="Study Directions" />
                                                                    </span>
                                                                    <span className="text">
                                                                        Study Field:
                                                                        <br />
                                                                        <strong>{field.studyField || 'N/A'}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                                <Row className="sub-card-row mb-0">
                                                    <Col md={6}>
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        <Image src={icBooks} alt="Specialist Areas" />
                                                                    </span>
                                                                    <span className="text">
                                                                        Specialist Area
                                                                        <br />
                                                                        <strong>{field.specialistAreas || 'N/A'}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>

                                                    <Col md={6}>
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        <Image src={icBooks} alt="Preferred Study Directions" />
                                                                    </span>
                                                                    <span className="text">
                                                                        Preferred Study Direction
                                                                        <br />
                                                                        <strong>{field.preferredStudyDirections || 'N/A'}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div>
                                        ))) : (
                                        <p>No study field information available.</p>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </>
    );
};

export default CompanyAddAreasDirections;
